import { useState } from 'react';
import { ListChecks, Check, X, SpinnerGap } from '@phosphor-icons/react';
import { getAuth } from 'firebase/auth';
import type { TrackingProtocolProposalEvent } from '@researcher/shared';
import { apiUrl } from '../../config/api';

interface Props {
  event: TrackingProtocolProposalEvent;
  savedStatus?: 'applied' | 'dismissed';
  onStatusChange?: (
    status: 'applied' | 'dismissed',
    applied?: { conditionLabel: string; schedule: string; items: string[] },
  ) => void;
}

export default function TrackingProtocolProposalCard({ event, savedStatus, onStatusChange }: Props) {
  const [status, setStatus] = useState<'pending' | 'applying' | 'applied' | 'dismissed'>(
    savedStatus || 'pending',
  );
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<{ schedule: string } | null>(null);

  if (status === 'dismissed') return null;

  const handleAccept = async () => {
    setStatus('applying');
    setError(null);
    try {
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken();
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await fetch(apiUrl('/tracking-protocols'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          condition: event.condition,
          time: event.time,
          timezone: browserTz,
          ...(event.customLabel ? { label: event.customLabel } : {}),
          ...(event.customItems && event.customItems.length > 0 ? { customItems: event.customItems } : {}),
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body.success) {
        throw new Error(body.error || 'Failed to set up tracking');
      }
      const schedule = `${body.data.schedule.frequency} at ${body.data.schedule.time}`;
      setApplied({ schedule });
      setStatus('applied');
      onStatusChange?.('applied', {
        conditionLabel: body.data.conditionLabel,
        schedule,
        items: body.data.items,
      });
    } catch (err: any) {
      console.error('[TrackingProtocolProposalCard] Failed to accept:', err);
      setError(err.message || 'Failed to set up tracking');
      setStatus('pending');
    }
  };

  const handleDismiss = () => {
    setStatus('dismissed');
    onStatusChange?.('dismissed');
  };

  return (
    <div className="mt-3 rounded-xl border border-pink-200 dark:border-pink-800 bg-pink-50 dark:bg-pink-900/20 overflow-hidden not-prose">
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-2 mb-2">
          <ListChecks size={18} weight="duotone" className="text-pink-500" />
          <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
            Daily check-in for <strong>{event.conditionLabel}</strong>
          </span>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">
          {event.frequency === 'daily' ? 'Daily' : 'Weekly'} at {event.time}. {event.items.length} item{event.items.length === 1 ? '' : 's'}:
        </p>
        <ul className="space-y-1 mb-3">
          {event.items.map((label, i) => (
            <li key={i} className="text-xs text-gray-700 dark:text-gray-200 flex items-start gap-1.5">
              <span className="text-pink-500 mt-0.5 flex-shrink-0">•</span>
              {label}
            </li>
          ))}
        </ul>

        {error && (
          <p className="text-xs text-red-600 dark:text-red-400 mb-2">{error}</p>
        )}

        {status === 'applied' ? (
          <div className="flex items-center gap-1.5 text-xs font-medium text-green-600 dark:text-green-400">
            <Check size={14} weight="bold" />
            Tracking set up{applied ? ` — ${applied.schedule}` : ''}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={handleAccept}
              disabled={status === 'applying'}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-orange-500 to-pink-500 rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all disabled:opacity-60"
            >
              {status === 'applying' ? (
                <SpinnerGap size={14} className="animate-spin" />
              ) : (
                <Check size={14} weight="bold" />
              )}
              {status === 'applying' ? 'Setting up...' : 'Start tracking'}
            </button>
            <button
              onClick={handleDismiss}
              disabled={status === 'applying'}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-60"
            >
              <X size={14} />
              Not now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
